import { useState } from 'react';

import type { ApiOrgId } from '#/api/types/ApiTypedId';
import { LegacyCloseButton } from '#/components/LegacyButton/LegacyCloseButton';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';
import type { OrgDashboardApiOrg } from '#/modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { colors } from '#/theme/theme';
import type { DatetimeString } from '#/types/TypedDateString';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import {
  getLocalStorageJsonItem,
  setLocalStorageJsonItem,
} from '#/utils/localStorage';
import { useLocalDateStringFn } from '#/utils/string/datetime/convertDatetimeString';
import { shortDate } from '#/utils/string/datetime/dateString';

function contactLink(i18n: I18nFn) {
  return (
    <StyledNativeLinkWithRef
      href={i18n('https://www.idealist.org/en/about/contact')}
      target="_blank"
      onClick={(event) =>
        trackAnchorClicked(event, 'Org Preview - Contact Idealist')
      }
    />
  );
}

type Props = {
  org: OrgDashboardApiOrg;
  now: DatetimeString;
};

export function OrgPreviewInfo({ org, now }: Props) {
  const { i18n, locale } = useI18n();
  const { localDateString } = useLocalDateStringFn();

  const previouslyDismissed =
    getLocalStorageJsonItem<Record<ApiOrgId, boolean>>(
      'orgPreviewInfoDismissed',
    ) ?? {};
  const [dismissed, setDismissed] = useState<boolean>(
    Boolean(previouslyDismissed[org.id]),
  );

  const closeInfo = () => {
    const dismissedList =
      getLocalStorageJsonItem<Record<ApiOrgId, boolean>>(
        'orgPreviewInfoDismissed',
      ) ?? {};
    setLocalStorageJsonItem('orgPreviewInfoDismissed', {
      ...dismissedList,
      [org.id]: true,
    });
    setDismissed(true);
  };

  if (dismissed) return null;

  const previewInfoMessage = (() => {
    const createdDate = shortDate(localDateString(org.created), {
      locale,
      yearFormat: 'long',
    });
    const submittedToday =
      createdDate ===
      shortDate(localDateString(now), { locale, yearFormat: 'long' });

    if (submittedToday) {
      if (org.is501c3) {
        return i18n(
          'Thank you! Your organization has been submitted for approval and will be reviewed within two business days. In the meantime, you can customize your profile, create a job listing, or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions.',
          { link: contactLink(i18n) },
        );
      }

      return i18n(
        'Thank you! Your organization has been submitted for approval and will be reviewed within two business days. In the meantime, you can customize your profile or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions.',
        { link: contactLink(i18n) },
      );
    }

    if (org.is501c3) {
      return i18n(
        'Your organization was submitted for approval on %(createdDate)s. In the meantime, you can customize your profile or create a job listing. If you have any questions or if it has been more than two business days since applying, please contact the {{link}}Idealist Community Support Team{{/link}}.',
        { createdDate, link: contactLink(i18n) },
      );
    }

    return i18n(
      'Your organization was submitted for approval on %(createdDate)s. In the meantime, you can customize your profile. If you have any questions or if it has been more than two business days since applying, please contact the {{link}}Idealist Community Support Team{{/link}}.',
      { createdDate, link: contactLink(i18n) },
    );
  })();

  return (
    <div
      style={{
        background: colors.softBlue,
        padding: 40,
        position: 'relative',
      }}
    >
      <div style={{ position: 'absolute', top: 12, right: 4 }}>
        <LegacyCloseButton onClick={closeInfo} />
      </div>

      <div
        style={{
          maxWidth: 1152,
          margin: '0 auto',
          fontSize: 16,
          lineHeight: 1.875,
        }}
      >
        {previewInfoMessage}
      </div>
    </div>
  );
}
